import {
  Body,
  ColumnHeaderCell,
  Header,
  Row,
  Table,
  Tag,
  Typography,
} from "@hellocarbo/lasagne";
import { FileSpreadsheetIcon } from "lucide-react";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import {
  ActionFunctionArgs,
  Form,
  LoaderFunctionArgs,
  useLoaderData,
} from "react-router";
import invariant from "tiny-invariant";

import { MatchingRow } from "@/components/ImportDeposit";
import Card from "@/components/ui/Card";
import PageWrapper from "@/components/ui/PageWrapper";
import { FeaturesContext } from "@/contexts";
import { TImportDeposit, TMatchingResponse } from "@/types";

import { useImportDepositContext } from "./ImportDepositSummary";

export async function loader({ params }: LoaderFunctionArgs) {
  const { scoreId, importId } = params;
  invariant(scoreId, "No scoreId found!");
  invariant(importId, "No importId found!");

  const importDepositResponse = await fetch(
    `/api/client/scores/${scoreId}/import_deposits/${importId}/`
  );
  if (importDepositResponse.status !== 200) {
    throw new Error("Failed to load this Import Deposit");
  }

  const { data: importDeposit } = (await importDepositResponse.json()) as {
    data: TImportDeposit;
  };

  const importDepositMatchingResponse = await fetch(
    `/api/client/scores/${scoreId}/import_deposits/${importId}/matching_entries`
  );
  if (importDepositMatchingResponse.status !== 200) {
    throw new Error("Failed to load the matching for this Import Deposit");
  }
  const { data: matchings } = (await importDepositMatchingResponse.json()) as {
    data: Array<TMatchingResponse>;
  };

  return { importDeposit, matchings };
}

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData();

  const scoreId = formData.get("scoreId");
  const importId = formData.get("importId");
  const id = formData.get("id");
  const selected_dr_unique_code = formData.get("selected_dr_unique_code");
  const status = formData.get("status");

  const response = await fetch(
    `/api/client/scores/${scoreId}/import_deposits/${importId}/matching_entries/${id}/`,
    {
      method: "PATCH",
      body: JSON.stringify({ data: { status, selected_dr_unique_code } }),
      headers: {
        "Content-Type": "application/json",
      },
    }
  );

  if (response.status !== 200) {
    throw new Error("Failed to update the selected code for this matching");
  }
}

function ImportDepositMatching() {
  const { t, i18n } = useTranslation("importDeposit");
  const { rawReferentials } = useImportDepositContext();

  const { importDeposit, matchings } = useLoaderData<typeof loader>();

  const referentials = rawReferentials.map((referential) => {
    return {
      label: i18n.language === "fr" ? referential.name_fr : referential.name_en,
      value: referential.unique_code,
    };
  });

  const { featureFlags } = useContext(FeaturesContext);
  if (!featureFlags || !featureFlags["import_to_verify_interface"]) {
    return null;
  }
  const matchingsRemaining = matchings.filter(
    (matching) => matching.status === "pending"
  ).length;

  return (
    <PageWrapper>
      <div className="mx-xl my-lg bg-grayscale-xx-light min-h-screen">
        <Card className="my-lg flex">
          <div className="flex flex-1 items-center">
            <FileSpreadsheetIcon className="mr-xs text-primary-light" />
            <Typography step="2" weight="semibold">
              {importDeposit.input_file}
            </Typography>
          </div>
          <div className="flex items-center justify-end">
            <Tag
              text={t("importDeposit:importMatching.matchesNumber", {
                x: `${t("formatter:number", { value: matchingsRemaining })}`,
                y: `${t("formatter:number", { value: matchings.length })}`,
              })}
              intent={"warning"}
            />
          </div>
        </Card>

        <Card className="my-lg">
          <Typography as="h4" step="2" weight="semibold" className="mb-md">
            {t("importDeposit:importMatching.matchesTitle")}
          </Typography>
          <Typography step="0" weight="normal" className="mb-md">
            {t("importDeposit:importMatching.matchesSubtitle")}
          </Typography>
          <Form>
            <Table>
              <Header>
                <Row>
                  <ColumnHeaderCell>
                    {t(`importDeposit:importMatchingTable.affectedProducts`)}
                  </ColumnHeaderCell>
                  <ColumnHeaderCell>
                    {t(`importDeposit:importMatchingTable.material`)}
                  </ColumnHeaderCell>
                  <ColumnHeaderCell>
                    {t(`importDeposit:importMatchingTable.emissionFactor`)}
                  </ColumnHeaderCell>
                  <ColumnHeaderCell className="w-3xl">
                    {t(`importDeposit:importMatchingTable.statusHeader`)}
                  </ColumnHeaderCell>
                  <ColumnHeaderCell className="text-right">
                    {t(`importDeposit:importMatchingTable.actions`)}
                  </ColumnHeaderCell>
                </Row>
              </Header>
              <Body>
                {matchings.map((matching) => (
                  <MatchingRow
                    matching={matching}
                    referentials={referentials}
                  />
                ))}
              </Body>
            </Table>
          </Form>
        </Card>
      </div>
    </PageWrapper>
  );
}

export default ImportDepositMatching;
